/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import {
  ItemWithAttachements,
  useAttachmentsContext,
} from '@/checkout-instore-legacy/components/Attachments/context/AttachmentsContext'
import isEmpty from 'lodash/isEmpty'
import { useDispatch } from 'react-redux'
import { addItems } from '@/checkout-instore-legacy/cart/actions'
import { getPriceInfo, getShowTogetherProductsVariables } from './utils'
// TODO: this error popped up when moving the checkout-instore project
// to checkout-instore-legacy, but needs to be fixed at some point
// @ts-ignore
import { InjectedRouter } from 'react-router'
import {
  Attachment,
  ShowTogetherItem,
} from '@/checkout-instore-legacy/product/typings'
import { getShowTogetherProducts } from '@/checkout-instore-legacy/product/service'
import { logger } from '@/checkout-instore-legacy/Logger'
import { openRootModalWithError } from '@/checkout-instore-legacy/rootModal/actions'
import { getIntlMessage } from '@/checkout-instore-legacy/i18n/utils'
import { sendMetric } from '@/checkout-instore-legacy/VtexLakeHouse'
import {
  MetricAction,
  MetricShowcase,
} from '@/checkout-instore-legacy/VtexLakeHouse/typings'

type UseShowTogetherProductsProps = {
  router: InjectedRouter
  sku: Item
}

export const useShowTogetherProducts = ({
  router,
  sku,
}: UseShowTogetherProductsProps) => {
  const { enableAttachments } = getInstoreConfig()

  const { setAttachmentState, setShowAttachments } = useAttachmentsContext()

  const dispatch = useDispatch()

  const [showTogetherProducts, setShowTogetherProducts] = useState<
    ShowTogetherItem[]
  >([])
  const [selectedItems, setSelectedItems] = useState<
    Record<string, ShowTogetherItem>
  >({})

  const mainItem: ShowTogetherItem = useMemo(
    () => ({
      id: sku.id,
      name: sku.name,
      images: sku.images ?? [],
      ean: sku.ean ?? undefined,
      sellers: sku.sellers as Partial<Seller>[],
      purchaseCondition: sku.purchaseConditionsPrices ?? [],
      attachments: sku.attachments as Attachment[],
      manualPrice: sku.manualPrice,
    }),
    [sku]
  )

  useEffect(() => {
    async function fetchShowTogetherProducts() {
      if (!sku.productId) return
      try {
        const variables = await getShowTogetherProductsVariables(sku.productId)
        const data = await getShowTogetherProducts(variables)
        const items = data.map((product) => product.defaultItem)
        setShowTogetherProducts(items)
      } catch (error) {
        logger.error({
          workflowType: 'error-get-show-together-products',
          error: error instanceof Error ? error : new Error(String(error)),
        })
        dispatch(
          openRootModalWithError({
            errorTitle: getIntlMessage('showTogether.queryError'),
            error,
            onRetry: () => window.location.reload(),
            onGiveUp: undefined,
            onRetryText: undefined,
            onGiveUpText: undefined,
          })
        )
      }
    }
    fetchShowTogetherProducts()
  }, [sku.productId])

  useEffect(() => {
    const itemsWithoutMainItem = Object.keys(selectedItems)
      .filter((k) => showTogetherProducts.find((p) => p.id === k))
      .reduce((acc, k) => ({ ...acc, [k]: selectedItems[k] }), {})
    setSelectedItems({ [mainItem.id]: mainItem, ...itemsWithoutMainItem })
  }, [sku])

  const totalPrice = useMemo(() => {
    return Object.values(selectedItems).reduce((acc, item) => {
      const { price } = getPriceInfo(item)
      if (!price) return acc
      return acc + price
    }, 0)
  }, [selectedItems])

  const addItemsToCart = (items: Record<string, ShowTogetherItem>) => {
    const itemsToAdd = Object.values(items).map((item) => ({
      id: item.id,
      quantity: 1,
      attachments: enableAttachments ? item.attachments : [],
      manualPrice: item.manualPrice,
    }))
    sendMetric({
      action: MetricAction.ShowTogetherProductsAddToCart,
      showcase: MetricShowcase.ShowTogetherProducts,
      items_add: itemsToAdd,
    })
    dispatch(addItems(itemsToAdd))
  }

  const checkHasAttachemtns = (item: ShowTogetherItem) =>
    item?.attachments && !isEmpty(item.attachments) && enableAttachments

  const handleAttachments = (
    item: ShowTogetherItem,
    customOnSubmit: (item: ItemWithAttachements) => void
  ) => {
    setAttachmentState({
      attachments: item.attachments as any,
      skuId: item.id,
      customOnSubmit: customOnSubmit,
    })
  }

  const addSelectedProductsToCart = () => {
    if (checkHasAttachemtns(mainItem)) {
      handleAttachments(mainItem, (itemWithAttachments) => {
        if (itemWithAttachments) {
          addItemsToCart({
            ...selectedItems,
            [mainItem.id]: {
              ...mainItem,
              ...itemWithAttachments,
              manualPrice: mainItem.manualPrice,
            },
          })
          router.push('/cart')
        }
      })
    } else {
      addItemsToCart(selectedItems)

      router.push('/cart')
    }
  }

  const selectItemWithAttachments = (item: ShowTogetherItem) =>
    handleAttachments(item, (itemWithAttachments) => {
      if (itemWithAttachments) {
        setSelectedItems((prev) => ({
          ...prev,
          [item.id]: {
            ...item,
            ...itemWithAttachments,
          },
        }))
        setShowAttachments(false)
      }
    })

  const selectItem = (item: ShowTogetherItem) => {
    const selections = selectedItems
    if (!selections[item.id]) {
      if (checkHasAttachemtns(item)) {
        selectItemWithAttachments(item)
        sendMetric({
          action: MetricAction.ShowTogetherProductsSelect,
          showcase: MetricShowcase.ShowTogetherProducts,
        })
      } else {
        selections[item.id] = item
        sendMetric({
          action: MetricAction.ShowTogetherProductsSelect,
          showcase: MetricShowcase.ShowTogetherProducts,
        })
      }
    } else {
      delete selections[item.id]
      sendMetric({
        action: MetricAction.ShowTogetherProductsUnchecked,
        showcase: MetricShowcase.ShowTogetherProducts,
      })
    }
    setSelectedItems((prev) => ({ ...prev, ...selections }))
  }

  return {
    showTogetherProducts: [mainItem, ...showTogetherProducts],
    totalPrice,
    selectedItems,
    selectItem,
    addSelectedProductsToCart,
  }
}
